<template>
  <b-card class="border-top-0 rounded-top-0">
    <div class="font-weight-bold font-size-h3 mb-4 mt-2 text-black">
      آمار کلی حساب ها
    </div>
    <div>
      <b-alert variant="danger" :show="Boolean(errorMessage)" class="mb-3">
        {{ errorMessage }}
      </b-alert>

      <b-row class="mb-3">
        <b-col cols="12" md="4" class="mb-6">
          <b-card class="bg-light-primary">
            <div
              class="d-flex align-items-center text-primary font-weight-bold balance-title"
            >
              <span>موجودی کل</span>
              <b-icon
                v-if="canReveal('totalBalance')"
                :icon="revealed.totalBalance ? 'eye' : 'eye-slash'"
                class="balance-title-eye"
                @click="toggleReveal('totalBalance')"
                @mouseleave="setReveal('totalBalance', false)"
              />
            </div>
            <div
              class="h4 mt-3 mb-0 text-primary font-weight-normal"
              :class="{
                'balance-value': canReveal('totalBalance'),
                'balance-value-masked':
                  !revealed.totalBalance && canReveal('totalBalance'),
              }"
              @click="setReveal('totalBalance', true)"
            >
              {{ displaySummaryValue("totalBalance") }}
            </div>
          </b-card>
        </b-col>
        <b-col cols="12" md="4" class="mb-6">
          <b-card class="bg-light-info">
            <div
              class="d-flex align-items-center text-info font-weight-bold balance-title"
            >
              <span>موجودی کل درحال انتقال</span>
              <b-icon
                v-if="canReveal('totalPendingTransfers')"
                :icon="revealed.totalPendingTransfers ? 'eye' : 'eye-slash'"
                class="balance-title-eye"
                @click="toggleReveal('totalPendingTransfers')"
                @mouseleave="setReveal('totalPendingTransfers', false)"
              />
            </div>
            <div
              class="h4 mt-3 mb-0 text-info font-weight-normal"
              :class="{
                'balance-value': canReveal('totalPendingTransfers'),
                'balance-value-masked':
                  !revealed.totalPendingTransfers &&
                  canReveal('totalPendingTransfers'),
              }"
              @click="setReveal('totalPendingTransfers', true)"
            >
              {{ displaySummaryValue("totalPendingTransfers") }}
            </div>
          </b-card>
        </b-col>
        <b-col cols="12" md="4">
          <b-card class="bg-light-success">
            <div
              class="d-flex align-items-center text-success font-weight-bold balance-title"
            >
              <span>موجودی کل قابل برداشت</span>
              <b-icon
                v-if="canReveal('netAvailableBalance')"
                :icon="revealed.netAvailableBalance ? 'eye' : 'eye-slash'"
                class="balance-title-eye"
                @click="toggleReveal('netAvailableBalance')"
                @mouseleave="setReveal('netAvailableBalance', false)"
              />
            </div>
            <div
              class="h4 mt-3 mb-0 text-success font-weight-normal"
              :class="{
                'balance-value': canReveal('netAvailableBalance'),
                'balance-value-masked':
                  !revealed.netAvailableBalance &&
                  canReveal('netAvailableBalance'),
              }"
              @click="setReveal('netAvailableBalance', true)"
            >
              {{ displaySummaryValue("netAvailableBalance") }}
            </div>
          </b-card>
        </b-col>
      </b-row>
    </div>
    <hr />
    <div class="d-flex justify-content-between align-items-center mb-2 mt-8">
      <div class="font-weight-bold font-size-h3 text-black">موجودی حساب ها</div>
      <div class="d-flex align-items-center balance-title">
        <span class="font-size-h6 font-weight-bold">نمایش مبلغ ها</span>
        <b-icon
          v-if="canReveal('tableAmounts')"
          :icon="revealed.tableAmounts ? 'eye' : 'eye-slash'"
          class="balance-title-eye"
          @click="toggleReveal('tableAmounts')"
          @mouseleave="setReveal('tableAmounts', false)"
        />
      </div>
    </div>
    <div>
      <b-table
        responsive
        striped
        hover
        show-empty
        :busy="isLoading"
        :items="providers"
        :fields="fields"
        empty-text="آیتمی جهت نمایش وجود ندارد"
        class="text-center"
      >
        <template v-slot:cell(balance)="row">
          <span
            :class="{
              'balance-value': canReveal('tableAmounts'),
              'balance-value-masked':
                !revealed.tableAmounts && canReveal('tableAmounts'),
            }"
            @click="setReveal('tableAmounts', true)"
          >
            {{ displayTableValue(row.item.balance) }}
          </span>
        </template>
        <template v-slot:cell(pendingTransfers)="row">
          <span
            :class="{
              'balance-value': canReveal('tableAmounts'),
              'balance-value-masked':
                !revealed.tableAmounts && canReveal('tableAmounts'),
            }"
            @click="setReveal('tableAmounts', true)"
          >
            {{ displayTableValue(row.item.pendingTransfers) }}
          </span>
        </template>
        <template v-slot:cell(availableBalance)="row">
          <span
            :class="{
              'balance-value': canReveal('tableAmounts'),
              'balance-value-masked':
                !revealed.tableAmounts && canReveal('tableAmounts'),
            }"
            @click="setReveal('tableAmounts', true)"
          >
            {{ displayTableValue(row.item.availableBalance) }}
          </span>
        </template>
        <template v-slot:cell(isBalanceFetchSuccessful)="row">
          <b-badge
            :variant="row.item.isBalanceFetchSuccessful ? 'success' : 'danger'"
            pill
          >
            {{ row.item.isBalanceFetchSuccessful ? "موفق" : "ناموفق" }}
          </b-badge>
        </template>
        <template v-slot:table-busy>
          <div class="text-center my-4">
            <b-spinner class="align-middle"></b-spinner>
            <strong class="mr-2">در حال دریافت اطلاعات...</strong>
          </div>
        </template>
      </b-table>
    </div>
  </b-card>
</template>

<script>
import httpHelper from "../helpers/httpHelper";
const EMPTY_VALUE = "-";

export default {
  name: "BankBalance",
  props: {
    gatewayPin: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      isLoading: false,
      errorMessage: null,
      providers: [],
      summary: {
        totalBalance: null,
        totalPendingTransfers: null,
        netAvailableBalance: null,
      },
      revealed: {
        totalBalance: false,
        totalPendingTransfers: false,
        netAvailableBalance: false,
        tableAmounts: false,
      },
      fields: [
        { key: "providerId", label: "شناسه" },
        { key: "providerName", label: "نام حساب پذیرنده" },
        { key: "balance", label: "موجودی" },
        { key: "pendingTransfers", label: "موجودی در حال انتقال" },
        { key: "availableBalance", label: "موجودی قابل برداشت" },
        { key: "isBalanceFetchSuccessful", label: "وضعیت دریافت موجودی" },
      ],
    };
  },
  mounted() {
    this.fetchBalances();
  },
  methods: {
    fetchBalances() {
      this.isLoading = true;
      this.errorMessage = null;

      httpHelper.setAuth();
      httpHelper
        .postData("~transfer/providers/balances/aggregate", {
          api: this.gatewayPin,
        })
        .then((response) => {
          const payload = response && response.data ? response.data : response;
          this.providers = payload.providers || [];
          this.summary = {
            totalBalance: payload.summary?.totalBalance ?? null,
            totalPendingTransfers:
              payload.summary?.totalPendingTransfers ?? null,
            netAvailableBalance: payload.summary?.netAvailableBalance ?? null,
          };
        })
        .catch((error) => {
          this.errorMessage =
            (error.response &&
              error.response.data &&
              (error.response.data.message || error.response.data.error)) ||
            "خطا در دریافت اطلاعات موجودی حساب ها.";
          this.providers = [];
          this.summary = {
            totalBalance: null,
            totalPendingTransfers: null,
            netAvailableBalance: null,
          };
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    formatNumber(value) {
      if (value === null || value === undefined || value === "") {
        return EMPTY_VALUE;
      }
      const numericValue = Number(value);
      return Number.isFinite(numericValue)
        ? numericValue.toLocaleString()
        : String(value);
    },
    hasNumericValue(value) {
      if (value == null || value === "") return false;
      return Number.isFinite(Number(value));
    },
    canReveal(key) {
      if (key === "tableAmounts") {
        return this.providers.some(
          (provider) =>
            this.hasNumericValue(provider.balance) ||
            this.hasNumericValue(provider.pendingTransfers) ||
            this.hasNumericValue(provider.availableBalance),
        );
      }
      const value = this.summary[key];
      return this.hasNumericValue(value);
    },
    maskText(text) {
      return "★".repeat(6);
    },
    displaySummaryValue(key) {
      const value = this.summary[key];
      if (value == null) return "نامشخص";
      const formatted =
        this.formatNumber(value) != "0"
          ? `${this.formatNumber(value)} ریال`
          : "0";
      if (!this.canReveal(key)) return formatted;
      return this.revealed[key] ? formatted : this.maskText(formatted);
    },
    displayTableValue(value) {
      const formatted = this.formatNumber(value);
      if (formatted === EMPTY_VALUE) return formatted;
      if (!this.canReveal("tableAmounts")) return formatted;
      return this.revealed.tableAmounts ? formatted : this.maskText(formatted);
    },
    setReveal(key, value) {
      if (!this.canReveal(key)) return;
      this.revealed[key] = value;
    },
    toggleReveal(key) {
      this.setReveal(key, !this.revealed[key]);
    },
  },
};
</script>

<style scoped>
.balance-title {
  gap: 0.5rem;
}

.balance-title-eye {
  color: #929292;
  margin-right: 3px;
  font-size: 1.8rem;
  flex-shrink: 0;
  cursor: pointer;
}

.balance-value {
  cursor: pointer;
  user-select: none;
}

.balance-value-masked {
  letter-spacing: 2px;
  font-family: monospace;
  transition: 0.2s ease;
  color: inherit;
}
</style>
